import { useEffect, useState } from "react";
import type { SiteShell } from "../server/shell";

type Owner = NonNullable<SiteShell["owner"]>;

const labels = {
  private: "Only you can see this",
  changed: "Changes not live yet",
  live: "Your site is live",
};

/**
 * The owner's way around their own site: where it stands, the dashboard, the editor and
 * publishing, which goes first until the site is live and again while saved changes wait for it.
 * It floats over the site rather than pushing the header down, so the page looks as visitors will
 * see it. Drawn only in the owner's private view, which shows their saved changes.
 */
export function OwnerBar({ owner }: { owner: Owner }) {
  // Edits made on the page save without a reload, and each save says so (lib/owner-drafts.ts).
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    const changed = () => setSaved(true);
    window.addEventListener("drate:draft-saved", changed);
    return () => window.removeEventListener("drate:draft-saved", changed);
  }, []);
  const status = !owner.published ? "private" : owner.changed || saved ? "changed" : "live";
  return (
    <div className="owner-bar" role="region" aria-label="Your site">
      {/* A status, so a save that changes it is announced. */}
      <p className="owner-bar-status" role="status">
        {status === "live" ? (
          <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="12" cy="12" r="5" fill="currentColor" />
          </svg>
        ) : status === "changed" ? (
          // The live dot, empty: a rounded rect, as site-layout.css colors the bar's circles green.
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <rect x="7" y="7" width="10" height="10" rx="5" />
          </svg>
        ) : (
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <rect x="5" y="11" width="14" height="10" rx="2" />
            <path d="M8 11V8a4 4 0 0 1 8 0v3" />
          </svg>
        )}
        <span className="owner-bar-label">{labels[status]}</span>
      </p>
      <span className="owner-bar-actions">
        <a className="owner-bar-action" href={owner.dashboard}>
          Dashboard
        </a>
        <a className="owner-bar-action" href={owner.edit}>
          Edit site
        </a>
        {/* Publish stands out while something waits to go live: on a phone, where the label is
            hidden, that carries the state. */}
        <a
          className={status === "live" ? "owner-bar-action" : "owner-bar-action owner-bar-primary"}
          href={owner.publish}
          aria-label={owner.published ? "Publish changes" : undefined}
        >
          Publish
        </a>
      </span>
    </div>
  );
}
